import React, { Component, Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { cardsState } from './data/payments';
import { isPublicPath } from '../../template/publicPaths';
import toast from '../../shared/Toast';
import { useOnboarding } from '../ImportJobContext';
import { INTRO_EVENT, getIntro, isIntroActive, isIntroDone, markIntroDone } from '../bailey-intro/introStage';
import { useSetup, SETUP_OPEN_EVENT, takePendingOpen } from './SetupContext';
import { publishPill } from './pillStore';
import { useStripeStatus } from '../../settings/stripeStatus';
import { heldCharges, minutesLeftLabel } from './setupPlan';
import { takeLocationHandoff } from './locationTrack';
import './setup.css';

const loadLayer = () => import('./SetupLayer');

// If setup can't load (a dropped connection, or a new version deployed while
// the app was open) it closes itself and says so, rather than taking the
// whole app down with it.
class LayerBoundary extends Component {
  constructor(props) { super(props); this.state = { failed: false }; }

  static getDerivedStateFromError() { return { failed: true }; }

  componentDidCatch() { if (this.props.onError) this.props.onError(); }

  render() { return this.state.failed ? null : this.props.children; }
}

/**
 * Router-level host for "Set up with Bailey": listens for anything asking to
 * open setup (Bailey's welcome, the sign-up redirect, Settings, the pill),
 * renders the layer, and keeps the resume pill on screen while setup is
 * unfinished, so it is never more than one tap away.
 *
 *   openSetup()                    carry on where they were
 *   openSetup({ chapter: 'paid' }) straight to one chapter
 *   openSetup({ park: true })      "Not now": keep it waiting, closed
 */

// A trip to Stripe that started before this page loaded means Stripe sent
// them back here. One that started after it (the phone apps open Stripe in
// the browser and stay put) is still being handled by the open chapter.
const PAGE_LOADED_AT = Date.now();

// Reception mode hands the device to the front desk: no setup there.
const receptionOn = () => {
  try { const s = JSON.parse(localStorage.getItem('receptionModeSettings') || 'null'); return !!(s && s.enabled); } catch (e) { return false; }
};

// A stylist or staff member who joined recently gets their plan offered
// on arrival; anyone who has been here a while only sees the pill.
function joinedRecently(user) {
  const info = (user && user.userInfo) || {};
  const h = info.hairdresser || {};
  const when = h.createdTime || h.createdAt || info.createdTime || info.createdAt || null;
  if (!when) return false;
  const t = new Date(when).getTime();
  return Number.isFinite(t) && Date.now() - t < 30 * 864e5;
}

// Setup's own entry in the browser history (see "Back closes setup" below).
const HISTORY_MARK = 'beuSetupOpen';

export default function SetupHost() {
  const setup = useSetup();
  const ob = useOnboarding() || {};
  const onboarding = ob.onboarding || null;
  const user = useSelector((s) => s.user);
  const location = useLocation();
  // A fresh lazy() for each try: React keeps a failed import for good.
  const [attempt, setAttempt] = useState(0);
  const SetupLayer = useMemo(() => lazy(loadLayer), [attempt]); // eslint-disable-line react-hooks/exhaustive-deps
  const [reception, setReception] = useState(receptionOn);
  const [introOn, setIntroOn] = useState(isIntroActive);
  const enabled = !!(setup && setup.enabled);

  useEffect(() => {
    const onReception = (e) => setReception(!!(e && e.detail && e.detail.enabled));
    const onIntro = () => setIntroOn(isIntroActive());
    window.addEventListener('receptionModeSettingsChanged', onReception);
    window.addEventListener(INTRO_EVENT, onIntro);
    return () => {
      window.removeEventListener('receptionModeSettingsChanged', onReception);
      window.removeEventListener(INTRO_EVENT, onIntro);
    };
  }, []);

  // Setup never stays open over the front desk.
  const { open: isOpen, close: closeSetup } = setup || {};
  useEffect(() => { if (reception && isOpen) closeSetup(); }, [reception, isOpen, closeSetup]);

  // Signing out clears this browser, but the server remembers that the
  // welcome was finished: put the local flag back so it never replays or
  // strands the desk pet. Read only once the server's copy is this person's.
  const welcomeDone = !!(setup && setup.welcomeDone);
  useEffect(() => {
    if (!welcomeDone || isIntroDone()) return;
    markIntroDone();
    try { window.dispatchEvent(new CustomEvent(INTRO_EVENT, { detail: getIntro() })); } catch (e) { /* ignore */ }
  }, [welcomeDone]);

  // Anything can ask to open setup, including before this was listening.
  useEffect(() => {
    if (!enabled) return undefined;
    const handle = (d) => {
      if (d.park) { setup.park(); return; }
      if (receptionOn()) return;
      setup.start({ chapter: d.chapter });
    };
    const on = (e) => { takePendingOpen(); handle((e && e.detail) || {}); };
    const early = takePendingOpen();
    if (early) handle(early);
    window.addEventListener(SETUP_OPEN_EVENT, on);
    return () => window.removeEventListener(SETUP_OPEN_EVENT, on);
  }, [enabled, setup]);

  // Back closes setup. Opening it adds one entry to the browser history, so
  // Back (the browser's, or Android's) takes setup off the screen instead of
  // moving the app underneath it. Closing it from inside takes the entry back
  // off again, when it's still the one on top. A chapter that sends them
  // somewhere in the app pushes its own entry over it, which stays.
  const entryPushed = useRef(false);
  useEffect(() => {
    if (!enabled || !isOpen) return undefined;
    try {
      const cur = window.history.state;
      if (!(cur && cur[HISTORY_MARK])) window.history.pushState({ ...(cur || {}), [HISTORY_MARK]: true }, '', window.location.href);
      entryPushed.current = true;
    } catch (e) { entryPushed.current = false; }
    const onPop = () => { entryPushed.current = false; closeSetup(); };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      if (!entryPushed.current) return;
      entryPushed.current = false;
      try { if (window.history.state && window.history.state[HISTORY_MARK]) window.history.back(); } catch (e) { /* ignore */ }
    };
  }, [enabled, isOpen, closeSetup]);

  // Setup never stays open over a public page (the booking page, signed out).
  useEffect(() => {
    if (isOpen && (isPublicPath(location.pathname) || !localStorage.getItem('accessToken'))) closeSetup();
  }, [isOpen, location.pathname, closeSetup]);

  // The layer hides the app header while it's up; make sure nothing keeps it
  // hidden once setup is closed, however it closed.
  useEffect(() => {
    if (!isOpen) { try { document.body.classList.remove('su-open'); } catch (e) { /* ignore */ } }
  }, [isOpen]);

  // An invited stylist or new member of staff: offer their own plan once,
  // on their first visit to the business side. Once started (or finished)
  // their own status on the server says so, so it never replays on another
  // login or device, and not at all where the server says they're already
  // established (setupEligible: false).
  const role = setup && setup.role;
  const status = setup && setup.state && setup.state.status;
  const offered = !!(setup && setup.offered);
  const serverStatus = setup && setup.serverStatus;
  useEffect(() => {
    if (!enabled || !offered || !['stylist', 'staff'].includes(role) || status !== 'new' || serverStatus !== 'new') return undefined;
    if (introOn || isPublicPath(location.pathname) || !localStorage.getItem('accessToken')) return undefined;
    if (!joinedRecently(user)) return undefined;
    const t = setTimeout(() => { if (!receptionOn()) setup.start({}); }, 900);
    return () => clearTimeout(t);
  }, [enabled, offered, role, status, serverStatus, introOn, location.pathname, user, setup]);

  // Back from Stripe (connecting, or adding a card): Stripe returns them to
  // the app with setup closed. Carry straight on in "Getting paid", once per
  // trip and only soon after it, so a later visit is never ambushed.
  const paidStep = (setup && (setup.state.steps || {}).paid) || {};
  const paidAnswers = (setup && ((setup.state.answers || {}).paid)) || {};
  const tripAt = [
    paidAnswers.stripeOpenedAt && paidStep.backCheckedFor !== paidAnswers.stripeOpenedAt ? paidAnswers.stripeOpenedAt : null,
    paidStep.cardOpenedAt && paidStep.cardCheckedFor !== paidStep.cardOpenedAt ? paidStep.cardOpenedAt : null,
  ].filter(Boolean).sort().pop() || '';
  const isOpen2 = !!(setup && setup.open);
  useEffect(() => {
    if (!enabled || isOpen2 || !tripAt || receptionOn()) return;
    const at = new Date(tripAt).getTime();
    const age = Date.now() - at;
    if (!(age >= 0 && age < 2 * 36e5) || at >= PAGE_LOADED_AT) return;
    if (isPublicPath(location.pathname) || !localStorage.getItem('accessToken')) return;
    // Only on the device that went: Getting paid leaves this marker in the
    // browser it sent to Stripe (the trip itself syncs to every device).
    try {
      if (localStorage.getItem('beu:setup-trip') !== tripAt) return;
      localStorage.removeItem('beu:setup-trip');
    } catch (e) { return; }
    setup.start({ chapter: 'paid' });
  }, [enabled, isOpen2, tripAt, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // A location just added and billed: the add-location page switched this
  // device here and left a note. Bailey carries on at the new site with the
  // location track, once this site's plan has loaded from the server.
  const setupReady = !!(setup && setup.ready);
  useEffect(() => {
    if (!enabled || !setupReady || role !== 'owner' || receptionOn()) return;
    let salonId = '';
    try { salonId = localStorage.getItem('salonId') || ''; } catch (e) { return; }
    const note = takeLocationHandoff(salonId);
    if (!note) return;
    setup.start({ track: 'location', seed: note.about ? { about: note.about } : null });
  }, [enabled, setupReady, role]); // eslint-disable-line react-hooks/exhaustive-deps

  // A new owner who left part-way through the welcome (closed the tab, lost
  // signal) still has the plan waiting: offered, never forced.
  const freshOwner = role === 'owner' && status === 'new' && !introOn
    && !!onboarding && ['new', 'in_progress'].includes(onboarding.status) && joinedRecently(user);

  // A modal or slide panel has the floor: the pill waits underneath.
  const pillCandidate = enabled && setup.ready && !setup.open && !reception && !!localStorage.getItem('accessToken') && !isPublicPath(location.pathname);

  // No longer drawn over the page, so an open panel doesn't hide it: the
  // bell counts it, and hiding it while the bell's own panel was open made
  // the count drop and come back (2, 1, 2).
  // The pill says hello once per session when it first appears.
  // Until they go live the pill stays, even when every step is "later".
  const showPill = pillCandidate && offered && (['active', 'parked'].includes(status) || freshOwner)
    && setup.total > 0;
  // After go-live: deposits or a policy chosen before card payments were
  // live are still waiting. Once payments go live, the pill comes back for
  // that one job (Stripe is only asked when something is held).
  const held = setup && setup.enabled ? heldCharges(setup.state.answers) : { any: false };
  const heldWaiting = enabled && role === 'owner' && status === 'done' && held.any;
  const stripeNow = useStripeStatus(null, heldWaiting);
  const bankAccount = useSelector((st) => st.bankDetails);
  // charges_enabled, not the derived key (data/payments.js cardsState).
  const heldPill = heldWaiting && !!stripeNow && cardsState(stripeNow, bankAccount) === 'live' && pillCandidate;
  const heldWhat = [held.deposits ? 'deposits' : '', held.policy ? 'cancellation policy' : ''].filter(Boolean).join(' and ');

  // Fetch setup's code while the pill is up, so a tap opens it at once.
  useEffect(() => { if (showPill || heldPill) loadLayer().catch(() => { /* tried again on open */ }); }, [showPill, heldPill]);

  // Setup isn't drawn over the page any more: SetupHost says what the way
  // back in should read, and the notification bell (its progress ring and the
  // panel's Bailey card) and the Bailey tab show it their own way.
  const parkedNow = (status === 'parked' || status === 'new') && !!setup && setup.settledCount === 0;
  // A chapter that sends them into the app can say what to do next:
  // setStep(id, { ..., pill: 'Move the practice booking' }).
  const curStep = setup && setup.current && ((setup.state.steps || {})[setup.current.id] || {});
  const stepHint = curStep && typeof curStep.pill === 'string' ? curStep.pill : '';
  const hint = stepHint;
  const pillKey = showPill ? `setup|${setup.doneCount}|${setup.total}|${setup.minutesLeft}|${parkedNow}|${(setup.current && setup.current.id) || ''}|${hint}`
    : heldPill ? `held|${heldWhat}` : '';
  const openRef = React.useRef(null);
  openRef.current = showPill
    ? () => setup.start({})
    : () => { setup.setStep('paid', { ...((setup.state.steps || {}).paid || {}), id: 'held' }); setup.start({ chapter: 'paid' }); };
  useEffect(() => {
    if (!pillKey) { publishPill(null); return; }
    if (pillKey.startsWith('held')) {
      publishPill({ kind: 'held', title: 'Card payments are live', detail: `Switch on your ${heldWhat}`, go: 'Open', open: () => openRef.current() });
      return;
    }
    publishPill({
      kind: 'setup',
      title: stepHint ? `${setup.current.title}: next step` : parkedNow ? 'Set up with Bailey' : 'Finish setting up',
      detail: hint || (parkedNow
        ? minutesLeftLabel(setup.minutesLeft).replace(' left', '')
        : `${setup.doneCount} of ${setup.total} done · ${minutesLeftLabel(setup.minutesLeft).replace('About ', 'about ').replace(' left', ' to go')}`),
      done: setup.doneCount,
      total: setup.total,
      go: parkedNow ? 'Start' : 'Continue',
      open: () => openRef.current(),
    });
  }, [pillKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => publishPill(null), []);

  if (!enabled) return null;

  return (
    <>
      {setup.open && (
        <LayerBoundary
          key={attempt}
          onError={() => {
            setup.close();
            setAttempt((n) => n + 1);
            toast.error('Setup didn’t open just now. Check your connection, then try again.');
          }}
        >
          <Suspense fallback={<div className="su-boot" role="status" aria-label="Opening setup"><span className="su-dots" aria-hidden="true"><i /><i /><i /></span></div>}>
            <SetupLayer />
          </Suspense>
        </LayerBoundary>
      )}
    </>
  );
}
